import React, { useState, useRef } from 'react';
import { projectApi } from '../../api/projectApi';
import { useProjectStore } from '../../contexts/ProjectContext';
import { usePostHog } from '@posthog/react';

export interface UploadWizardDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onUploadComplete?: (scheduleData: any) => void;
}

const UploadWizardDrawer: React.FC<UploadWizardDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  onUploadComplete
}) => {
  const [step, setStep] = useState<'upload' | 'processing' | 'success'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [scheduleData, setScheduleData] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const posthog = usePostHog();
  
  // Get refreshProjectSchedules from the project store
  const { refreshProjectSchedules } = useProjectStore();

  // Minimum display times (ms)
  const MIN_PROCESSING_TIME = 1500;
  const MIN_SUCCESS_TIME = 1000;
  const processingStartRef = useRef<number | null>(null);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  // Handle drag and drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  };

  // Prevent default behavior for drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    // Check file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (fileExtension !== 'xer') {
      setError('Only XER files are supported');
      return;
    }

    // Start processing
    setStep('processing');
    setProgress(0);
    setError(null);
    processingStartRef.current = Date.now();
    posthog?.capture('upload_started', { project_id: projectId, type: 'schedule' });

    try {
      // Upload the file using the API
      const response = await projectApi.uploadXerFile(projectId, file, {
        name: file.name.replace('.xer', ''),
        description: 'Uploaded schedule',
        status: 'Current'
      });

      console.log('Upload response:', response);

      // The upload is now synchronous, so we can proceed directly
      setProgress(100);
      setStep('success');

      // Show success state for at least MIN_SUCCESS_TIME
      await new Promise(resolve => setTimeout(resolve, MIN_SUCCESS_TIME));

    } catch (error) {
      console.error('Upload failed:', error);
      setError('Failed to upload file. Please try again.');
      setStep('upload');
      setProgress(0);
    }
  };

  // Upload is now synchronous, so no status checking needed

  // Handle completion
  const handleComplete = async () => {
    try {
      // Add a small delay to ensure the backend has time to complete the schedule creation
      console.log('Waiting for backend to complete schedule creation...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Refresh the project schedules to get the newly created schedule
      console.log('Refreshing project schedules after upload completion');
      await refreshProjectSchedules(projectId);
      
      if (onUploadComplete && scheduleData) {
        onUploadComplete(scheduleData);
      }
    } catch (error) {
      console.error('Error refreshing schedules after upload:', error);
    }
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    setFile(null);
    setError(null);
    setProgress(0);
    setStep('upload');

    setScheduleData(null);
    onClose();
  };

  // If drawer is closed, don't render anything
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {step === 'upload' && 'Upload Schedule'}
            {step === 'processing' && 'Processing Schedule'}
            {step === 'success' && 'Upload Complete'}
          </h2>
          <button 
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 p-6">
          {/* Upload Step */}
          {step === 'upload' && (
            <>
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6 hover:border-[#nb-accent-primary] transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                
                <p className="text-gray-600 mb-2">
                  {file ? file.name : 'Drag and drop your XER file here'}
                </p>
                <p className="text-gray-500 text-sm mb-4">
                  {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'or click to browse'}
                </p>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".xer"
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Browse Files
                </button>
              </div>
              
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
                  <div className="flex">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 mb-6">
                <div className="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Supported file format: .xer (Primavera P6 export)</span>
                </div>
              </div>
            </>
          )}
          
          {/* Processing Step */}
          {step === 'processing' && (
            <>
              <div className="mb-6">
                <p className="text-gray-600 mb-2">Processing {file?.name}</p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div 
                    className="bg-[#nb-accent-primary] h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500">{progress}% complete</p>
                
              </div>
              
              <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4">
                <div className="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Please wait while we process your XER file. This may take a few minutes for large files.</span>
                </div>
              </div>
            </>
          )}
          
          {/* Success Step */}
          {step === 'success' && (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Successful!</h3>
                <p className="text-gray-600 mb-4">
                  Your XER file has been processed and the schedule has been created.
                </p>
                
                {scheduleData && (
                  <div className="bg-gray-50 rounded-lg p-4 text-left">
                    <h4 className="font-medium text-gray-900 mb-2">Schedule Details</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Name:</span> {scheduleData.name || 'N/A'}</p>
                      <p><span className="font-medium">Description:</span> {scheduleData.description || 'N/A'}</p>
                      <p><span className="font-medium">Status:</span> {scheduleData.status && scheduleData.status.toLowerCase() === 'current' ? 'Current' : (scheduleData.status ? scheduleData.status.charAt(0).toUpperCase() + scheduleData.status.slice(1) : 'N/A')}</p>
                      <p><span className="font-medium">Activities:</span> {scheduleData.activity_count || 'N/A'}</p>
                      <p><span className="font-medium">Start Date:</span> {scheduleData.start_date ? new Date(scheduleData.start_date).toLocaleDateString() : 'N/A'}</p>
                      <p><span className="font-medium">End Date:</span> {scheduleData.end_date ? new Date(scheduleData.end_date).toLocaleDateString() : 'N/A'}</p>
                      <p><span className="font-medium">Duration:</span> {scheduleData.duration || 'N/A'} days</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          {step === 'upload' && (
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file}
                className="px-4 py-2 bg-[#nb-accent-primary] text-white rounded-md hover:bg-[#nb-accent-blue] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Upload Schedule
              </button>
            </div>
          )}
          
          {step === 'processing' && (
            <div className="text-center">
              <p className="text-sm text-gray-500">Processing in progress...</p>
            </div>
          )}
          
          {step === 'success' && (
            <div className="flex justify-end">
              <button
                onClick={handleComplete}
                className="px-4 py-2 bg-[#nb-accent-primary] text-white rounded-md hover:bg-[#nb-accent-blue] transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadWizardDrawer;
